// src/stores/AuthStore.js (Pinia)
import { defineStore } from "pinia"

// The CompSetStore tracks the projects that a user has selected for comparison at the given time.
export const useCompSetStore = defineStore("CompSetStore", {
  id: 'compSetStore',
  state: () => ({
    // sorted array of project ids to be used.
    includedProjects: [],
    allProjects: [],
    compQuery: null,
    mapQuery: null,
    mapExpandedQuery: null,
    location: null,
    showMarkers: null,
    selectedProjects: {},
    escalation: null,
    center: { lat: 40.4000, lng: -74.5000 }, // Default center
    zoom: 9, // Default zoom
    scopeSearchTerms: [], // Array of selected DDA terms for scope search
    useScopeSearch: false, // Whether scope search mode is enabled
    scopeFilteredProjectIds: [], // Project IDs that have data for the selected scope
  }),
  getters: {
    getAllProjects: (state) => {
      return state.allProjects
    },
    getIncludedProjects: (state) => {
      return state.includedProjects
    },
    getSelectedProjects: (state) => {
      return state.selectedProjects
    },
    // Map has to show everything that fits compsearch and the map as a whole
    getQueryMapExpanded: (state) => {
      const ampersand = state.compQuery && state.mapExpandedQuery ? '&' : ''
      return `${state.compQuery || ''}${ampersand}${state.mapExpandedQuery || ''}`
    },
    // CompSearch needs to query only what is in the encircled data
    getMapQuery: (state) => {
      return state.mapQuery
    },
    // CompSearch needs to query only what is in the encircled data
    // Note: If mapQuery has location, it overrides any location in compQuery
    getQuery: (state) => {
      let compQuery = state.compQuery || '';
      const mapQuery = state.mapQuery || '';
      
      // If mapQuery contains location, remove location from compQuery to avoid duplicates
      if (mapQuery.includes('location=') && compQuery.includes('location=')) {
        // Remove location parameter from compQuery
        compQuery = compQuery.replace(/[&]?location=[^&]*/, '');
        // Clean up any leading/trailing ampersands
        compQuery = compQuery.replace(/^&+|&+$/g, '');
      }
      
      const ampersand = compQuery && mapQuery ? '&' : '';
      return `${compQuery}${ampersand}${mapQuery}`;
    },
    getLocation: (state) => {
      return state.location
    },
    getEscalation: (state) => {
      if (!state.escalation) { return state.escalation}

      return parseFloat(String(state.escalation)).toFixed(2)
    },
    getShowMarkers: (state) => {
      return state.showMarkers
    },
    getScopeSearchTerms: (state) => {
      return state.scopeSearchTerms
    },
    getUseScopeSearch: (state) => {
      return state.useScopeSearch
    },
    getScopeFilteredProjectIds: (state) => {
      return state.scopeFilteredProjectIds
    },
  },
  actions: {
    setCompQuery(compQuery) {
      this.compQuery = compQuery
    },
    setMapQuery(mapQuery) {
      // Debug logging to track what's overwriting the mapQuery
      console.log('🔍 setMapQuery called with:', mapQuery);
      console.log('🔍 Previous mapQuery was:', this.mapQuery);
      console.trace('🔍 setMapQuery call stack:');
      this.mapQuery = mapQuery
    },
    // wider boundary map
    setMapExpandedQuery(mapExpandedQuery) {
      this.mapExpandedQuery = mapExpandedQuery
    },
    setAllProjects(projects) {
      this.allProjects = projects
    },
    setIncludedProjects(projects) {
      this.includedProjects = projects
    },
    toggleProjectId(id) {
      // Maintain array of which projects are selected
      const idxInProjects = this.includedProjects.findIndex((obj) => obj.id === id)
      if (idxInProjects > -1) {
        this.includedProjects.splice(idxInProjects, 1)
      } else {
        this.includedProjects.push(id)
      }

      if (this.allProjects.indexOf(id) < 0) {
        this.allProjects.push(id)
      }

      // Maintain sorting here as it is used elsewhere to determine if the selected projects have changed
      this.includedProjects = this.includedProjects.sort()
      this.allProjects = this.allProjects.sort()
    },
    setLocation(value) {
      this.location = value
    },
    setEscalation(escalation) {
      this.escalation = escalation
    },
    setShowMarkers(value) {
      this.showMarkers = value
    },
    setSelectedProjects(value) {
      this.selectedProjects = value;
    },
    toggleProjectSelection(projectId) {
      this.selectedProjects[projectId] = !this.selectedProjects[projectId];
    },
    isProjectSelected(projectId) {
      return !!this.selectedProjects[projectId];
    },
    setCenter(center) {
      this.center = center;
    },
    setZoom(zoom) {
      this.zoom = zoom;
    },
    // You could also add an action to set both at once if needed:
    setMapState(center, zoom) {
      this.center = center;
      this.zoom = zoom;
    },
    // Or an action to reset to defaults
    resetMapState() {
        this.center = { lat: 40.4000, lng: -74.5000 };
        this.zoom = 9;
    },
    setScopeSearchTerms(terms) {
      this.scopeSearchTerms = terms;
    },
    setUseScopeSearch(value) {
      this.useScopeSearch = value;
    },
    setScopeFilteredProjectIds(ids) {
      this.scopeFilteredProjectIds = ids;
    },
    // Clear all comp set data
    clearCompSetData() {
        this.compQuery = null;
        this.mapQuery = null;
        this.mapExpandedQuery = null;
        this.location = null;
        this.showMarkers = null;
        this.selectedProjects = {};
        this.escalation = null;
        this.scopeSearchTerms = [];
        this.useScopeSearch = false;
        this.scopeFilteredProjectIds = [];
        this.resetMapState();
    }
  },
})
